import qs from 'qs'
import { getSignedUrl, uploadFileToS3, uploadImage } from './service'

// eslint-disable-next-line import/no-internal-modules
import { maxImageFileSize } from '@insided/deprecated-libs/constants'

let uniqueId = 0

export class FileSizeError extends Error {}
export class TotalFileSizeError extends Error {}
export class UploadError extends Error {
  private readonly attachmentId
  constructor (attachmentId: number) {
    super()
    this.attachmentId = attachmentId
  }
}
export class FileTypeError extends Error {
  private readonly attachmentId
  constructor (attachmentId: number) {
    super()
    this.attachmentId = attachmentId
  }
}

function isImageType (type: string) {
  const imageTypes = [
    'image/png', 'image/jpg', 'image/jpeg', 'image/gif',
    'png', 'jpg', 'jpeg', 'gif'
  ]
  if (imageTypes.includes(type)) return true
  return type.indexOf('image') === 0
}

class AttachmentUploader {
  private readonly maxCombinedAttachmentsSize
  private readonly maxAttachmentSize
  private readonly maxImageSize
  private totalAttachmentSize

  constructor () {
    this.maxCombinedAttachmentsSize = 104857600 // 100Mb
    this.maxAttachmentSize = 52428800 // 50Mb
    this.maxImageSize = maxImageFileSize
    this.totalAttachmentSize = 0
  }

  getUniqueAttachmentId () {
    return uniqueId++
  }

  getType (file: File) {
    if (isImageType(file.type)) {
      return 'image'
    }
    return file.name.split('.').pop()
  }

  async uploadFile (attachmentId: number, file: File) {
    if (isImageType(file.type)) {
      return await this._uploadImage(attachmentId, file)
    }
    return await this._uploadFileToS3(attachmentId, file)
  }

  async _uploadImage (attachmentId: number, file: File) {
    const tokenEl = document.getElementById('csrftoken') as HTMLInputElement
    const csrfToken = tokenEl?.value
    if (file.size && file.size > this.maxImageSize) {
      throw new FileSizeError()
    }

    const formData = new window.FormData()
    formData.append('image', file)
    formData.append('yip_csrf_token', csrfToken)

    try {
      const data = await uploadImage(formData)
      return {
        source: data.source,
        source_thumb: data.source_thumb
      }
    } catch (err) {
      throw new UploadError(attachmentId)
    }
  }

  async _uploadFileToS3 (attachmentId: number, file: File) {
    if (file.size && file.size > this.maxAttachmentSize) {
      throw new FileSizeError()
    }

    if (this.totalAttachmentSize + (file.size || 0) > this.maxCombinedAttachmentsSize) {
      throw new TotalFileSizeError()
    }
    this.totalAttachmentSize += file.size || 0

    const fileType = this.getFileType(file)

    try {
      const fileData = await getSignedUrl(file.name, fileType)
      const tags = qs.parse(fileData.url, { ignoreQueryPrefix: true })['x-amz-tagging'] as string
      await uploadFileToS3(fileData.url, file, fileType, tags)
      return fileData
    } catch (err) {
      if (err.response.data === 'File extension is not allowed') {
        throw new FileTypeError(attachmentId)
      }
      throw new UploadError(attachmentId)
    }
  }

  getFileType (file: File) {
    let fileType = file.type

    if (!fileType && /^.+\.(xls)$/.test(file.name)) {
      fileType = 'application/vnd.ms-excel'
    }

    if (!fileType && /^.+\.(csv)$/.test(file.name)) {
      fileType = 'text/csv'
    }

    return fileType
  }
}

export default AttachmentUploader
